import { EQUIPMENT_STATUSES, type ShipTime } from '@felix/domain';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  FileButton,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Tabs,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBook2,
  IconCheck,
  IconDeviceFloppy,
  IconPlayerPlay,
  IconPlus,
  IconSend,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Controller,
  type FieldPath,
  type UseFormReturn,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatDay } from '../../format.js';
import { DateTimeInput } from '../../inputs/DateTimeInput.js';
import { Panel } from '../../Panel.js';
import { useShell, useUnsavedChanges } from '../../shell-context.js';
import { DrillSourceLink } from '../drills/DrillFindings.js';
import { errorCode } from '../personnel/source.js';
import { ReturnedBanner } from '../returned/Returned.js';
import { ApprovalPanel } from './ApprovalPanel.js';
import { AttachmentCard } from './AttachmentCard.js';
import type { Measurement, MeasurementPoint } from './condition-types.js';
import { measurementStepMode, pointsForWorkOrder } from './condition-view.js';
import { utcToShipLocal } from './counter-form.js';
import { usePmsErrorText } from './EquipmentDrawer.js';
import { MeasurementsStep } from './MeasurementsStep.js';
import classes from './Pms.module.css';
import work from './PmsWork.module.css';
import { LoadFailed, PmsBadge, PmsPriorityBadge, RowsSkeleton } from './PmsWorkParts.js';
import { numberFormat, WORK_ORDER_STEPS, workOrderDueDate, workOrderStep } from './plan-view.js';
import { ReportDialog } from './ReportDialog.js';
import {
  emptyReportForm,
  type ReportFormValues,
  reportFormOf,
  reportFormSchema,
  reportOverdue,
  toMeasurementEntries,
  toWorkRecordForm,
} from './report-form.js';
import type { PmsSource } from './source.js';
import { workOrderLate, workOrderStatus } from './status-tone.js';
import type {
  HistoryEntry,
  JobView,
  PendingItem,
  PmsAttachment,
  SparePartOption,
  WorkOrderView,
} from './types.js';
import { VoidCorrectDialog } from './VoidCorrectDialog.js';

export interface WorkOrderDetailProps {
  source: PmsSource;
  vesselId: string;
  timeZone: ShipTime;
  woId: string;
  jobs: readonly JobView[];
  pending: readonly PendingItem[];
  me: { id: string; name: string } | null;
  now: () => Date;
  /** Yazmadan sonra liste ve zil tazelenir. */
  onChanged: () => void;
  /** İş emri numarası breadcrumb'a yazılır. */
  onLoaded?: (wo: WorkOrderView) => void;
  /** Kişinin yetkisi (gemide; `work_orders.edit`, `work_records.create`); düğmeler buna göre. */
  can?: { edit: boolean; report: boolean };
}

type Data =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | {
      kind: 'ready';
      wo: WorkOrderView;
      entries: HistoryEntry[];
      attachments: PmsAttachment[];
      /** Kayda sonradan eklenen CM raporları (ADR 0029 Ek M7f F4). */
      recordAttachments: PmsAttachment[];
      /** Geminin ölçüm noktaları (Measurements adımı; M7c). */
      points: MeasurementPoint[];
      /** İş emrine bağlı kayıtlı ölçümler (raporlanmışsa). */
      recorded: Measurement[];
    };

const TABS = ['safety', 'maintenance', 'measurements', 'spareParts', 'photos', 'remarks'] as const;

/** Kaydedilmemiş rapor taslağı bu bilgisayarda (sunucuda taslak kayıt yok; K7). */
const draftKey = (woId: string) => `felix.pms.reportDraft.${woId}`;

function readDraft(woId: string): ReportFormValues | null {
  try {
    const text = globalThis.localStorage?.getItem(draftKey(woId));
    if (!text) return null;
    // M7c'den önceki taslakta ölçüm alanı, M7f'den öncekinde gecikme ve CBM alanları yok
    const draft = JSON.parse(text) as Partial<ReportFormValues>;
    return {
      ...draft,
      measurements: draft.measurements ?? {},
      overdueReason: draft.overdueReason ?? '',
      cmReportPending: draft.cmReportPending === true,
      // ADR 0132'den önceki taslakta bakım öncesi durum ve adam-saat yok
      conditionBefore: draft.conditionBefore ?? '',
      workHours: draft.workHours ?? '',
    } as ReportFormValues;
  } catch {
    return null;
  }
}

function writeDraft(woId: string, values: ReportFormValues | null) {
  try {
    if (values) globalThis.localStorage?.setItem(draftKey(woId), JSON.stringify(values));
    else globalThis.localStorage?.removeItem(draftKey(woId));
  } catch {
    // Tarayıcı deposu kapalıysa taslak yalnız ekranda kalır
  }
}

/**
 * İş emri detayı (`/pms/work-orders?wo=<id>`; plan M7 Task 14b; `menu-yapisi.md` "Düzen
 * ayrıntıları", `sefer-noon-report.jpeg` dili): adım şeridi (Assigned → … → Closed), sekmeli form
 * (güvenlik, bakım, ölçümler, parçalar, fotoğraflar, not), sağda özet, talimat, ekler ve onay;
 * altta "Save Draft" ve birincil "Complete & Report" (parola; K7). Raporlanmış kayıt salt
 * okunurdur; düzeltme "Correct" ile [NR496 4.5.3f]. Ofiste bütün detay salt okunurdur (K13).
 */
export function WorkOrderDetail(props: WorkOrderDetailProps) {
  const { source, woId, vesselId, timeZone } = props;
  const { t } = useTranslation();
  const errorText = usePmsErrorText();
  const [data, setData] = useState<Data>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);
  const [tab, setTab] = useState<(typeof TABS)[number]>('safety');
  const [correcting, setCorrecting] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [voiding, setVoiding] = useState<'void' | 'correct' | null>(null);
  const [notice, setNotice] = useState<{ tone: 'red' | 'green'; text: string } | null>(null);
  const [parts, setParts] = useState<SparePartOption[]>([]);
  const [busy, setBusy] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `reloads` yazmadan sonra yeniden okutur
  useEffect(() => {
    let live = true;
    setData((current) =>
      current.kind === 'ready' && current.wo.id === woId ? current : { kind: 'loading' },
    );
    (async () => {
      if (!source.workOrder) throw Object.assign(new Error('NOT_FOUND'), { code: 'NOT_FOUND' });
      const wo = await source.workOrder(woId);
      const [history, attachments, points, recorded, recordAttachments] = await Promise.all([
        source.history
          ? source.history(vesselId, wo.jobId ? { job: wo.jobId } : { equipment: wo.equipmentId })
          : Promise.resolve([]),
        source.attachments ? source.attachments(woId) : Promise.resolve([]),
        source.measurementPoints
          ? source
              .measurementPoints(vesselId)
              .then((list) => list.items)
              .catch(() => [])
          : Promise.resolve([]),
        source.measurements && wo.record
          ? source.measurements(vesselId, { workOrderId: woId }).catch(() => [])
          : Promise.resolve([]),
        source.attachments && wo.record
          ? source.attachments(wo.record.id, 'work_record').catch(() => [])
          : Promise.resolve([]),
      ]);
      return {
        wo,
        entries: history.filter((e) => e.workOrderId === woId),
        attachments,
        points,
        recorded,
        recordAttachments,
      };
    })()
      .then((ready) => {
        if (!live) return;
        setData({ kind: 'ready', ...ready });
        props.onLoaded?.(ready.wo);
      })
      .catch((error: unknown) => {
        if (live) setData({ kind: 'error', code: errorCode(error) });
      });
    return () => {
      live = false;
    };
  }, [source, woId, vesselId, reloads]);

  useEffect(() => {
    if (!source.work?.spareParts) return;
    let live = true;
    source.work
      .spareParts(vesselId)
      .then((list) => {
        if (live) setParts(list);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [source, vesselId]);

  const ready = data.kind === 'ready' ? data : null;
  const wo = ready?.wo ?? null;
  const job = wo?.jobId ? (props.jobs.find((j) => j.id === wo.jobId) ?? null) : null;
  const live = wo?.record ? (ready?.entries.find((e) => e.id === wo.record?.id) ?? null) : null;
  const ship = source.scope === 'ship' && Boolean(source.work);
  const shell = useShell();
  const can = props.can ?? { edit: true, report: true };
  const editable =
    ship &&
    can.report &&
    wo !== null &&
    wo.status !== 'cancelled' &&
    (wo.record === null || correcting);
  const serverNow = props.now().toISOString();
  const condition = useMemo(
    () =>
      wo
        ? pointsForWorkOrder(ready?.points ?? [], wo, job)
        : { points: [], required: new Set<string>() },
    [wo, ready?.points, job],
  );
  // Düzeltmede ölçümler salt okunur ve zorunluluk aranmaz (K14; ADR 0029 Ek S35)
  const step = measurementStepMode({ editable, recorded: Boolean(wo?.record), correcting });
  const requiredPoints = step.enforceRequired ? [...condition.required] : [];
  const requiredKey = requiredPoints.sort().join(',');
  // Gecikme (F1, F3) raporlama gününe (gemi bugünü) ve girilen sayaç değerine göre
  const shipToday = utcToShipLocal(serverNow, timeZone).slice(0, 10);
  // Durum bazlı iş: rapor eki ya da "sağlayıcıdan bekleniyor" beyanı (F4) [NR496 4.5.4e]
  const cmRequired = job?.jobType === 'condition_based' || condition.required.size > 0;
  const cmAttached = (ready?.attachments ?? []).some((a) => a.kind === 'report' && !a.voided);

  // biome-ignore lint/correctness/useExhaustiveDependencies: zorunlu noktalar `requiredKey` ile izlenir
  const schema = useMemo(
    () =>
      reportFormSchema({
        job,
        kind: wo?.kind ?? 'planned',
        timeZone,
        serverNow,
        requiredPoints,
        ...(wo
          ? {
              overdue: (values: ReportFormValues) =>
                reportOverdue(wo, shipToday, values.meterHours),
            }
          : {}),
        cmReport: { required: cmRequired, attached: cmAttached },
      }),
    [job, wo, timeZone, serverNow, requiredKey, shipToday, cmRequired, cmAttached],
  );
  const form = useForm<ReportFormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyReportForm({ job, now: serverNow, timeZone, me: props.me }),
  });
  useUnsavedChanges(editable && form.formState.isDirty);
  const overdue = wo ? reportOverdue(wo, shipToday, form.watch('meterHours')) : false;

  // Form iş emri yüklenince kurulur: kayıt varsa kaydın değerleri, yoksa bu bilgisayardaki taslak
  const formKey = `${wo?.id ?? ''}|${wo?.record?.id ?? ''}|${correcting}|${job?.id ?? ''}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: form yalnız iş emri ya da kip değişince kurulur
  useEffect(() => {
    if (!wo) return;
    if (live) {
      form.reset(reportFormOf(live, job, timeZone));
      return;
    }
    form.reset(
      readDraft(wo.id) ??
        emptyReportForm({
          job,
          now: serverNow,
          timeZone,
          me: props.me,
          safety: (job?.safetyItems ?? []).map(
            (_, i) => wo.safetyCheck?.items?.find((item) => item.index === i)?.checked === true,
          ),
        }),
    );
  }, [formKey]);

  const fail = useCallback(
    (error: unknown) => setNotice({ tone: 'red', text: errorText(error) }),
    [errorText],
  );
  const changed = () => {
    setReloads((n) => n + 1);
    props.onChanged();
  };

  if (data.kind === 'loading') {
    return (
      <Panel>
        <RowsSkeleton rows={10} />
      </Panel>
    );
  }
  if (data.kind === 'error' || !wo) {
    return (
      <Panel>
        <LoadFailed
          code={data.kind === 'error' ? data.code : 'NOT_FOUND'}
          onRetry={() => setReloads((n) => n + 1)}
        />
      </Panel>
    );
  }

  const status = workOrderStatus(wo);
  const late = workOrderLate(wo);
  const safetyItems = (job?.safetyItems ?? []).map((text, index) => ({ text, index }));
  const start = async () => {
    if (!source.work) return;
    setBusy(true);
    try {
      await source.work.start(wo.id, wo.rowVersion);
      changed();
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };
  const saveDraft = async () => {
    const values = form.getValues();
    writeDraft(wo.id, values);
    form.reset(values);
    // Güvenlik kontrolü iş emrine yazılır (PMS belgesi §12): adım şeridi ilerler
    const items = values.safety.map((checked, index) => ({ index, checked }));
    const saved = wo.safetyCheck?.items ?? [];
    if (
      source.work &&
      wo.status === 'in_progress' &&
      items.length > 0 &&
      JSON.stringify(items) !== JSON.stringify(saved)
    ) {
      try {
        await source.work.safetyCheck(wo.id, wo.rowVersion, items);
        changed();
      } catch (error) {
        fail(error);
        return;
      }
    }
    setNotice({ tone: 'green', text: t('pms.work.detail.draftSaved') });
  };
  const submitReport = form.handleSubmit(
    () => {
      setNotice(null);
      if (correcting) setVoiding('correct');
      else setReporting(true);
    },
    (errors) => {
      // Eksik durum parametresi: ölçüm adımı açılır (ADR 0029 C3)
      if (errors.measurements) setTab('measurements');
    },
  );
  const measurementValues = form.watch('measurements') ?? {};
  const measurementError = form.formState.errors.measurements?.message;

  return (
    <div className={work.detailGrid}>
      <div className={work.detailMain}>
        <Panel>
          <div className={work.woHeader}>
            <div className={work.woTitle}>
              <h2>
                {wo.woNo} · {wo.title}
              </h2>
              <span className={work.badgeRow}>
                <PmsPriorityBadge priority={wo.priority} />
                <PmsBadge status={status} />
                {late ? <PmsBadge status="overdue" /> : null}
                {wo.due.postponementPending ? (
                  <PmsBadge status="scheduled">{t('pms.work.plan.postponementPending')}</PmsBadge>
                ) : null}
              </span>
            </div>
            <span className={work.note}>
              {wo.equipmentName ?? '—'}
              {job ? ` · ${job.code}` : ''}
            </span>
            {wo.drillSource ? (
              <DrillSourceLink source={wo.drillSource} vesselId={ship ? null : vesselId} />
            ) : null}
          </div>
          <WorkOrderSteps wo={wo} />
          {ship && shell ? (
            <div className={work.headerActions}>
              {/* Bakım sırasında arıza (LGS-17 A4; ADR 0096 §6): kaynak iş emri bağlanır */}
              {/* Açık turuncu zeminde turuncu yazı 12 px'te 4.5:1 kontrastı tutmaz (axe); düğme
                  nötr, uyarı rengi yalnız ikonda */}
              <Button
                variant="default"
                size="xs"
                leftSection={
                  <IconAlertTriangle size={14} color="var(--mantine-color-orange-filled)" />
                }
                onClick={() => shell.navigate(`/pms/defects?new=1&wo=${wo.id}`)}
              >
                {t('pms.work.detail.reportDefect')}
              </Button>
            </div>
          ) : null}
        </Panel>

        {notice ? (
          <Alert
            color={notice.tone}
            withCloseButton
            // Kapatma düğmesinin adı (axe button-name)
            closeButtonLabel={t('pms.work.detail.dismiss')}
            onClose={() => setNotice(null)}
            role="alert"
          >
            {notice.text}
          </Alert>
        ) : null}
        {/* İade bandı gönderene ve görene (ADR 0116 Ek): iade eden, zaman, gerekçe */}
        {wo.record?.status === 'returned' && !correcting ? (
          <ReturnedBanner notice={wo.record.returnNotice} timeZone={timeZone} hint={ship} />
        ) : null}
        {wo.record && !correcting ? (
          <Alert color="blue" icon={<IconAlertTriangle size={16} />}>
            {t(ship ? 'pms.work.detail.readOnly' : 'pms.work.detail.officeReadOnly')}
          </Alert>
        ) : !ship ? (
          <Alert color="blue">{t('pms.work.detail.officeReadOnly')}</Alert>
        ) : null}
        {correcting ? (
          <Alert color="orange">{t('pms.work.void.correcting', { wo: wo.woNo })}</Alert>
        ) : null}

        <Panel>
          <Tabs
            value={tab}
            onChange={(value) => setTab((value as (typeof TABS)[number]) ?? 'safety')}
            className={work.pageTabs}
            keepMounted
          >
            <Tabs.List>
              {TABS.map((key, index) => (
                <Tabs.Tab key={key} value={key}>
                  {`${index + 1}. ${t(`pms.work.detail.tabs.${key}`)}`}
                </Tabs.Tab>
              ))}
            </Tabs.List>
            <Tabs.Panel value="safety">
              <SafetyTab form={form} items={safetyItems} readOnly={!editable} />
            </Tabs.Panel>
            <Tabs.Panel value="maintenance">
              <MaintenanceTab
                form={form}
                job={job}
                readOnly={!editable}
                timeZone={timeZone}
                serverNow={serverNow}
                overdue={editable && overdue}
              />
            </Tabs.Panel>
            <Tabs.Panel value="measurements">
              <MeasurementsStep
                points={condition.points}
                required={condition.required}
                values={measurementValues}
                {...(step.showRecorded ? { recorded: data.recorded } : {})}
                {...(step.editable
                  ? {
                      onChange: (pointId: string, value: number | '') =>
                        form.setValue(
                          'measurements',
                          { ...form.getValues('measurements'), [pointId]: value },
                          { shouldDirty: true },
                        ),
                    }
                  : {})}
                error={
                  measurementError
                    ? t(`pms.errors.${measurementError}`, {
                        defaultValue: t('pms.errors.VALIDATION_FAILED'),
                      })
                    : undefined
                }
              />
            </Tabs.Panel>
            <Tabs.Panel value="spareParts">
              <SparePartsTab form={form} options={parts} readOnly={!editable} />
            </Tabs.Panel>
            <Tabs.Panel value="photos">
              {cmRequired || form.watch('cmReportPending') ? (
                <CmReportField
                  form={form}
                  readOnly={!editable}
                  attached={cmAttached}
                  later={data.recordAttachments}
                  {...(source.attachmentUrl ? { hrefOf: source.attachmentUrl } : {})}
                  {...(ship &&
                  !correcting &&
                  live?.cmReportPending === true &&
                  live.status !== 'voided' &&
                  source.work?.uploadAttachment
                    ? {
                        onUpload: async (file: File) => {
                          try {
                            const recordId = wo.record?.id;
                            if (recordId) {
                              await source.work?.uploadAttachment?.(recordId, file, 'work_record');
                            }
                            setReloads((n) => n + 1);
                            props.onChanged();
                          } catch (error) {
                            fail(error);
                          }
                        },
                      }
                    : {})}
                />
              ) : null}
              <PhotosTab
                attachments={data.attachments}
                {...(source.attachmentUrl ? { hrefOf: source.attachmentUrl } : {})}
                {...(editable && !correcting && source.work?.uploadAttachment
                  ? {
                      onUpload: async (file: File) => {
                        try {
                          await source.work?.uploadAttachment?.(wo.id, file);
                          setReloads((n) => n + 1);
                        } catch (error) {
                          fail(error);
                        }
                      },
                    }
                  : {})}
              />
            </Tabs.Panel>
            <Tabs.Panel value="remarks">
              <Textarea
                label={t('pms.work.detail.remarks')}
                autosize
                minRows={4}
                readOnly={!editable}
                {...form.register('remarks')}
              />
            </Tabs.Panel>
          </Tabs>
        </Panel>

        {editable ? (
          <div className={work.bottomBar}>
            {wo.status === 'planned' && !correcting && can.edit ? (
              <Button
                variant="default"
                leftSection={<IconPlayerPlay size={16} />}
                loading={busy}
                onClick={() => void start()}
              >
                {t('pms.work.detail.start')}
              </Button>
            ) : null}
            {correcting ? (
              <Button variant="default" onClick={() => setCorrecting(false)}>
                {t('pms.drawer.cancel')}
              </Button>
            ) : (
              <Button
                variant="default"
                leftSection={<IconDeviceFloppy size={16} />}
                onClick={() => void saveDraft()}
              >
                {t('pms.work.detail.saveDraft')}
              </Button>
            )}
            <Button
              leftSection={<IconSend size={16} />}
              disabled={!correcting && wo.status !== 'in_progress'}
              onClick={() => void submitReport()}
            >
              {correcting ? t('pms.work.void.submitCorrection') : t('pms.work.detail.complete')}
            </Button>
          </div>
        ) : null}
      </div>

      <aside className={work.detailSide}>
        <Panel title={t('pms.work.detail.summary')}>
          <dl className={classes.detailRows}>
            {(
              [
                ['woNo', wo.woNo],
                ['kind', t(`pms.work.detail.kinds.${wo.kind}`, { defaultValue: wo.kind })],
                ['job', job ? `${job.code} · ${job.title}` : wo.title],
                ['component', wo.equipmentName ?? '—'],
                ['dueDate', formatDay(workOrderDueDate(wo))],
                ...(wo.due.dueHours !== null
                  ? [['dueHours', t('pms.hours', { value: numberFormat('en', wo.due.dueHours) })]]
                  : []),
                ['assignedTo', wo.assignedToName ?? '—'],
                ['startedAt', wo.startedAt ? formatDateTime(wo.startedAt) : '—'],
              ] as [string, string][]
            ).map(([key, value]) => (
              <div key={key} className={classes.detailRow}>
                <dt>{t(`pms.work.detail.fields.${key}`)}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </Panel>
        <InstructionCard wo={wo} />
        <Panel title={t('pms.work.attachments.title')}>
          {data.attachments.length === 0 ? (
            <p className={work.note}>{t('pms.work.attachments.none')}</p>
          ) : (
            <ul className={work.attachmentList}>
              {data.attachments.map((attachment) => (
                <AttachmentCard
                  key={attachment.id}
                  attachment={attachment}
                  {...(source.attachmentUrl ? { href: source.attachmentUrl(attachment.id) } : {})}
                />
              ))}
            </ul>
          )}
        </Panel>
        {wo.record ? (
          <ApprovalPanel
            record={wo.record}
            entry={live}
            canDecide={ship && props.pending.some((p) => p.recordId === wo.record?.id && !p.office)}
            canVoid={
              ship &&
              !correcting &&
              live !== null &&
              live.status !== 'voided' &&
              live.createdBy === source.userId
            }
            onDecide={async (input) => {
              if (!wo.record) return;
              await source.work?.decideRecord(wo.record.id, input);
              changed();
            }}
            onVoid={() => setVoiding('void')}
            onCorrect={() => {
              setCorrecting(true);
              setTab('maintenance');
            }}
          />
        ) : null}
      </aside>

      <ReportDialog
        opened={reporting}
        woNo={wo.woNo}
        onClose={() => setReporting(false)}
        onConfirm={async (password) => {
          if (!source.work) return;
          const values = form.getValues();
          await source.work.report(wo.id, {
            ...(password ? { password } : {}),
            ...(values.safety.length > 0
              ? {
                  safetyCheck: {
                    items: values.safety.map((checked, index) => ({ index, checked })),
                  },
                }
              : {}),
            record: toWorkRecordForm(values, timeZone),
            measurements: toMeasurementEntries(values, timeZone),
          });
          writeDraft(wo.id, null);
          form.reset(values);
          setReporting(false);
          setNotice({ tone: 'green', text: t('pms.work.detail.reported') });
          changed();
        }}
      />
      <VoidCorrectDialog
        opened={voiding !== null}
        mode={voiding ?? 'void'}
        woNo={wo.woNo}
        onClose={() => setVoiding(null)}
        onConfirm={async ({ reason, password }) => {
          if (!source.work || !wo.record) return;
          if (voiding === 'correct') {
            await source.work.correctRecord(wo.record.id, {
              reason,
              ...(password ? { password } : {}),
              record: toWorkRecordForm(form.getValues(), timeZone),
            });
            setCorrecting(false);
          } else {
            await source.work.voidRecord(wo.record.id, reason, password);
          }
          setVoiding(null);
          changed();
        }}
      />
    </div>
  );
}

/**
 * Adım şeridi (PMS belgesi §12): Assigned → Started → Safety Check → Maintenance → Measurements →
 * Spare Parts → Photos → Completed → Chief Engineer Approval → Closed. Tamamlananlar onaylı,
 * bulunulan adım `aria-current="step"`; iade edilen kayıtta onay adımı kırmızı.
 */
export function WorkOrderSteps({
  wo,
}: {
  wo: Pick<WorkOrderView, 'status' | 'record' | 'startedAt' | 'safetyCheck'>;
}) {
  const { t } = useTranslation();
  const step = workOrderStep(wo);
  const status = workOrderStatus(wo);
  return (
    <ol className={work.steps} aria-label={t('pms.work.detail.stepsLabel')}>
      {WORK_ORDER_STEPS.map((key, index) => (
        <li
          key={key}
          data-state={index < step ? 'done' : index === step ? 'current' : undefined}
          data-returned={(key === 'approval' && status === 'returned') || undefined}
          aria-current={index === step ? 'step' : undefined}
        >
          <span className={work.stepDot} aria-hidden>
            {index < step ? <IconCheck size={12} stroke={3} /> : index + 1}
          </span>
          <span className={work.stepLabel}>{t(`pms.work.detail.steps.${key}`)}</span>
        </li>
      ))}
    </ol>
  );
}

/** Talimat kartı (K16): gemide yoksa uyarı ve pasif "View Manual". */
export function InstructionCard({ wo }: { wo: Pick<WorkOrderView, 'instruction'> }) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.work.detail.instruction.${key}`);
  const { instruction } = wo;
  const linked = instruction.revisionId !== null || instruction.missing;
  return (
    <Panel title={k('title')} icon={<IconBook2 size={18} stroke={1.8} />}>
      <div className={work.drawerStack}>
        {!linked ? (
          <p className={work.note}>{k('none')}</p>
        ) : instruction.missing ? (
          <Alert color="orange" icon={<IconAlertTriangle size={16} />} title={k('missing')}>
            {k('missingHint')}
          </Alert>
        ) : (
          <PmsBadge status="completed">{k('onBoard')}</PmsBadge>
        )}
        {instruction.supersededSinceOpen ? <p className={work.note}>{k('superseded')}</p> : null}
        <Button
          variant="default"
          size="xs"
          disabled={!linked || instruction.missing}
          leftSection={<IconBook2 size={14} />}
        >
          {k('viewManual')}
        </Button>
      </div>
    </Panel>
  );
}

export function SafetyTab({
  form,
  items,
  readOnly,
}: {
  form: UseFormReturn<ReportFormValues>;
  items: { text: string; index: number }[];
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const message = form.formState.errors.safety?.message;
  if (items.length === 0) return <p className={work.note}>{t('pms.work.detail.safetyNone')}</p>;
  return (
    <Stack gap="xs" mt="sm">
      <p className={work.note}>{t('pms.work.detail.safetyHint')}</p>
      {items.map((item) => (
        <Controller
          key={item.index}
          control={form.control}
          name={`safety.${item.index}`}
          render={({ field }) => (
            <Checkbox
              label={item.text}
              checked={field.value === true}
              disabled={readOnly}
              onChange={(event) => field.onChange(event.currentTarget.checked)}
            />
          )}
        />
      ))}
      {message ? (
        <p className={work.error} role="alert">
          {t(`pms.errors.${message}`)}
        </p>
      ) : null}
    </Stack>
  );
}

function useFieldError(form: UseFormReturn<ReportFormValues>) {
  const { t } = useTranslation();
  return (name: string) => {
    let node: unknown = form.formState.errors;
    for (const part of name.split('.'))
      node = (node as Record<string, unknown> | undefined)?.[part];
    const message = (node as { message?: string } | undefined)?.message;
    return message
      ? t(`pms.errors.${message}`, {
          defaultValue: t(`pms.fieldErrors.${message}`, {
            defaultValue: t('pms.errors.VALIDATION_FAILED'),
          }),
        })
      : undefined;
  };
}

export function MaintenanceTab({
  form,
  job,
  readOnly,
  timeZone,
  serverNow,
  overdue = false,
}: {
  form: UseFormReturn<ReportFormValues>;
  job: JobView | null;
  readOnly: boolean;
  timeZone: ShipTime;
  serverNow: string;
  /** Gecikmiş iş: gecikme gerekçesi zorunlu (ADR 0029 Ek M7f F1) [NR496 4.5.4b]. */
  overdue?: boolean;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.work.detail.maintenance.${key}`, values);
  const error = useFieldError(form);
  const performers = useFieldArray({ control: form.control, name: 'performers' });
  const checklist = job?.checklist ?? [];
  const answers = [
    { value: 'pass', label: k('answers.pass') },
    { value: 'fail', label: k('answers.fail') },
    { value: 'na', label: k('answers.na') },
  ];
  return (
    <Stack gap="md" mt="sm">
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Controller
          control={form.control}
          name="doneAt"
          render={({ field }) => (
            <DateTimeInput
              label={k('doneAt')}
              required
              value={field.value}
              onChange={field.onChange}
              maxDate={utcToShipLocal(serverNow, timeZone)}
              readOnly={readOnly}
              error={error('doneAt')}
            />
          )}
        />
        <Controller
          control={form.control}
          name="meterHours"
          render={({ field }) => (
            <NumberInput
              label={k('meterHours')}
              required={(job?.interval.runningHours ?? null) !== null}
              min={0}
              decimalScale={1}
              value={field.value}
              onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
              readOnly={readOnly}
              error={error('meterHours')}
            />
          )}
        />
      </SimpleGrid>
      <fieldset className={classes.fieldset}>
        <legend>{k('performedBy')}</legend>
        <Stack gap={6}>
          {performers.fields.map((item, index) => (
            <div key={item.id} className={work.performerRow}>
              <TextInput
                aria-label={k('performer', { n: index + 1 })}
                readOnly={readOnly}
                {...form.register(`performers.${index}.name`)}
              />
              {readOnly || performers.fields.length < 2 ? null : (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label={k('removePerformer', { n: index + 1 })}
                  onClick={() => performers.remove(index)}
                >
                  <IconTrash size={15} />
                </ActionIcon>
              )}
            </div>
          ))}
        </Stack>
        {readOnly ? null : (
          <Button
            variant="subtle"
            size="xs"
            mt={6}
            leftSection={<IconPlus size={14} />}
            onClick={() => performers.append({ userId: null, name: '' })}
          >
            {k('addPerformer')}
          </Button>
        )}
        {error('performers') ? (
          <p className={work.error} role="alert">
            {error('performers')}
          </p>
        ) : null}
      </fieldset>
      <Textarea
        label={k('workPerformed')}
        required
        autosize
        minRows={3}
        readOnly={readOnly}
        {...form.register('workPerformed')}
        error={error('workPerformed')}
      />
      <fieldset className={classes.fieldset}>
        <legend>{k('checklist')}</legend>
        {checklist.length === 0 ? (
          <p className={work.note}>{k('checklistNone')}</p>
        ) : (
          <ol className={work.checkList}>
            {checklist.map((item, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: maddenin yeri iş tanımında sabit; cevap sırayla eşleşir
              <li key={`${index}-${item.text}`}>
                <span className={work.checkText}>{item.text}</span>
                <Controller
                  control={form.control}
                  name={`checklist.${index}.answer`}
                  render={({ field }) => (
                    <SegmentedControl
                      size="xs"
                      aria-label={k('answerOf', { item: item.text })}
                      data={answers}
                      value={field.value}
                      onChange={field.onChange}
                      disabled={readOnly}
                    />
                  )}
                />
                {item.kind === 'value' ? (
                  <Controller
                    control={form.control}
                    name={`checklist.${index}.value`}
                    render={({ field }) => (
                      <NumberInput
                        aria-label={k('valueOf', { item: item.text })}
                        placeholder={item.unit ?? k('value')}
                        rightSection={
                          item.unit ? <span className={work.unit}>{item.unit}</span> : null
                        }
                        decimalScale={3}
                        value={field.value}
                        onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
                        readOnly={readOnly}
                        error={error(`checklist.${index}.value`)}
                        className={work.valueInput}
                      />
                    )}
                  />
                ) : null}
                <TextInput
                  aria-label={k('remarkOf', { item: item.text })}
                  placeholder={k('remark')}
                  readOnly={readOnly}
                  {...form.register(`checklist.${index}.remark`)}
                  error={error(`checklist.${index}.remark`)}
                  className={work.remarkInput}
                />
              </li>
            ))}
          </ol>
        )}
        {error('checklist') ? (
          <p className={work.error} role="alert">
            {error('checklist')}
          </p>
        ) : null}
      </fieldset>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Controller
          control={form.control}
          name="result"
          render={({ field }) => (
            <div>
              <span className={classes.fieldLabel}>{k('result')}</span>
              <SegmentedControl
                fullWidth
                aria-label={k('result')}
                data={[
                  { value: 'pass', label: t('pms.work.results.pass') },
                  { value: 'fail', label: t('pms.work.results.fail') },
                ]}
                value={field.value}
                onChange={field.onChange}
                disabled={readOnly}
              />
              {error('result') ? (
                <p className={work.error} role="alert">
                  {error('result')}
                </p>
              ) : null}
            </div>
          )}
        />
        <Controller
          control={form.control}
          name="conditionAfter"
          render={({ field }) => (
            <Select
              label={k('conditionAfter')}
              required
              data={EQUIPMENT_STATUSES.map((s) => ({ value: s, label: t(`pms.statuses.${s}`) }))}
              value={field.value || null}
              onChange={(value) => field.onChange(value ?? '')}
              clearable
              clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
              readOnly={readOnly}
              error={error('conditionAfter')}
            />
          )}
        />
        {/* İsteğe bağlı (ADR 0132 §2): DNV MAD `conditionbefore` ve `workhours` */}
        <Controller
          control={form.control}
          name="conditionBefore"
          render={({ field }) => (
            <Select
              label={k('conditionBefore')}
              description={readOnly ? undefined : k('optional')}
              data={EQUIPMENT_STATUSES.map((s) => ({ value: s, label: t(`pms.statuses.${s}`) }))}
              value={field.value || null}
              onChange={(value) => field.onChange(value ?? '')}
              clearable
              clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
              readOnly={readOnly}
              error={error('conditionBefore')}
            />
          )}
        />
        <Controller
          control={form.control}
          name="workHours"
          render={({ field }) => (
            <NumberInput
              label={k('workHours')}
              description={readOnly ? undefined : k('optional')}
              min={0}
              max={10_000}
              decimalScale={2}
              allowNegative={false}
              value={field.value}
              onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
              readOnly={readOnly}
              error={error('workHours')}
            />
          )}
        />
      </SimpleGrid>
      {overdue || form.watch('overdueReason') || error('overdueReason') ? (
        <Textarea
          label={k('overdueReason')}
          description={readOnly ? undefined : k('overdueHint')}
          required={!readOnly}
          autosize
          minRows={2}
          readOnly={readOnly}
          error={error('overdueReason')}
          {...form.register('overdueReason')}
        />
      ) : null}
    </Stack>
  );
}

/**
 * Durum bazlı işin CM tanı/öngörü raporu (ADR 0029 Ek M7f F4) [NR496 4.5.4e]: iş emrine rapor
 * (PDF) eklenir ya da "servis sağlayıcıdan bekleniyor" beyan edilir. Beyanlı kayıtta rapor
 * sonradan kayda eklenir; kaydın içeriği değişmez [NR496 4.5.3f].
 */
export function CmReportField({
  form,
  readOnly,
  attached,
  later,
  hrefOf,
  onUpload,
}: {
  form: UseFormReturn<ReportFormValues>;
  readOnly: boolean;
  attached: boolean;
  later: readonly PmsAttachment[];
  hrefOf?: (id: string) => string;
  onUpload?: (file: File) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.work.detail.cmReport.${key}`);
  const error = useFieldError(form);
  const [uploading, setUploading] = useState(false);
  const pending = form.watch('cmReportPending');
  const received = later.filter((a) => !a.voided);
  return (
    <Stack gap="xs" mt="sm">
      <span className={classes.fieldLabel}>{k('title')}</span>
      <span className={work.note}>{attached ? k('attached') : k('hint')}</span>
      <Controller
        control={form.control}
        name="cmReportPending"
        render={({ field }) => (
          <Checkbox
            label={k('pending')}
            checked={field.value === true}
            onChange={(event) => field.onChange(event.currentTarget.checked)}
            disabled={readOnly}
            error={error('cmReportPending')}
          />
        )}
      />
      {readOnly && pending && received.length === 0 ? (
        <Alert color="orange" icon={<IconAlertTriangle size={16} />}>
          {k('awaited')}
        </Alert>
      ) : null}
      {received.length > 0 ? (
        <ul className={work.attachmentList} aria-label={k('received')}>
          {received.map((attachment) => (
            <AttachmentCard
              key={attachment.id}
              attachment={attachment}
              {...(hrefOf ? { href: hrefOf(attachment.id) } : {})}
            />
          ))}
        </ul>
      ) : null}
      {onUpload && received.length === 0 ? (
        <div className={work.uploadRow}>
          <FileButton
            accept="image/jpeg,image/png,application/pdf"
            onChange={async (file) => {
              if (!file) return;
              setUploading(true);
              await onUpload(file);
              setUploading(false);
            }}
          >
            {(buttonProps) => (
              <Button
                {...buttonProps}
                variant="light"
                leftSection={<IconUpload size={16} />}
                loading={uploading}
              >
                {k('upload')}
              </Button>
            )}
          </FileButton>
        </div>
      ) : null}
    </Stack>
  );
}

export function SparePartsTab({
  form,
  options,
  readOnly,
}: {
  form: UseFormReturn<ReportFormValues>;
  options: readonly SparePartOption[];
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.work.detail.spareParts.${key}`, values);
  const parts = useFieldArray({ control: form.control, name: 'spareParts' });
  const byId = new Map(options.map((o) => [o.materialId, o]));
  if (readOnly && parts.fields.length === 0) return <p className={work.note}>{k('none')}</p>;
  return (
    <Stack gap="sm" mt="sm">
      <p className={work.note}>{k('hint')}</p>
      {parts.fields.map((item, index) => {
        const name = `spareParts.${index}.materialId` as FieldPath<ReportFormValues>;
        const option = byId.get(form.watch(`spareParts.${index}.materialId`));
        return (
          <div key={item.id} className={work.partRow}>
            <Controller
              control={form.control}
              name={name}
              render={({ field }) => (
                <Select
                  aria-label={k('part', { n: index + 1 })}
                  placeholder={k('part', { n: index + 1 })}
                  searchable
                  // Uzun listede ad ya da kodla aranır (ADR 0122 §4)
                  limit={100}
                  nothingFoundMessage={k('nothingFound')}
                  data={options.map((o) => ({ value: o.materialId, label: o.label }))}
                  value={(field.value as string) || null}
                  onChange={(value) => field.onChange(value ?? '')}
                  readOnly={readOnly}
                  description={
                    option && option.onHand !== null
                      ? k('onHand', { qty: option.onHand, unit: option.unit })
                      : undefined
                  }
                />
              )}
            />
            <Controller
              control={form.control}
              name={`spareParts.${index}.quantity`}
              render={({ field }) => (
                <NumberInput
                  aria-label={k('quantity', { n: index + 1 })}
                  min={0}
                  decimalScale={2}
                  value={field.value}
                  onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
                  readOnly={readOnly}
                  className={work.qtyInput}
                />
              )}
            />
            {readOnly ? null : (
              <ActionIcon
                variant="subtle"
                color="red"
                aria-label={k('remove', { n: index + 1 })}
                onClick={() => parts.remove(index)}
              >
                <IconTrash size={15} />
              </ActionIcon>
            )}
          </div>
        );
      })}
      {readOnly ? null : (
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={() => parts.append({ materialId: '', quantity: 1 })}
        >
          {k('add')}
        </Button>
      )}
    </Stack>
  );
}

export function PhotosTab({
  attachments,
  hrefOf,
  onUpload,
}: {
  attachments: readonly PmsAttachment[];
  hrefOf?: (id: string) => string;
  onUpload?: (file: File) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.work.detail.photos.${key}`);
  const [uploading, setUploading] = useState(false);
  return (
    <Stack gap="sm" mt="sm">
      {onUpload ? (
        <div className={work.uploadRow}>
          <FileButton
            accept="image/jpeg,image/png,application/pdf"
            onChange={async (file) => {
              if (!file) return;
              setUploading(true);
              await onUpload(file);
              setUploading(false);
            }}
          >
            {(buttonProps) => (
              <Button
                {...buttonProps}
                variant="light"
                leftSection={<IconUpload size={16} />}
                loading={uploading}
              >
                {k('upload')}
              </Button>
            )}
          </FileButton>
          <span className={work.note}>{k('hint')}</span>
        </div>
      ) : null}
      {attachments.length === 0 ? (
        <p className={work.note}>{t('pms.work.attachments.none')}</p>
      ) : (
        <ul className={work.attachmentList}>
          {attachments.map((attachment) => (
            <AttachmentCard
              key={attachment.id}
              attachment={attachment}
              {...(hrefOf ? { href: hrefOf(attachment.id) } : {})}
            />
          ))}
        </ul>
      )}
    </Stack>
  );
}
